import { useCallback } from 'react';
import { Button, Flex, Box, Text, Stack, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { UserRoundMinus, UserRoundPlus } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { RouterOutputs, trpcAdmin } from '../../lib/trpcAdmin';
import { BulkTransferConsultantModal } from './BulkTransferConsultantModal';

export function ProviderConsultantSettings({
  providerId,
  member,
}: {
  providerId: string | number;
  member: NonNullable<RouterOutputs['providers']['user']['consultantById']>;
}) {
  const toast = useToast();
  const utilsAdmin = trpcAdmin.useUtils();
  const transferModal = useDisclosure();

  const { data: assignmentCounts } =
    trpcAdmin.providers.user.consultantActiveAssignmentCounts.useQuery(
      {
        providerId: Number(providerId),
        consultantId: member.id,
      },
      { enabled: Boolean(providerId) && Boolean(member.id) },
    );

  const deleteConsultant = trpcAdmin.providers.user.disableConsultant.useMutation({
    onSuccess: () => {
      toast({
        title: !member.deleted_at
          ? t`Consultant deleted successfully.`
          : t`Consultant re-enabled successfully.`,
        status: 'success',
        isClosable: true,
      });

      void utilsAdmin.providers.user.listConsultantsWithPagination.refetch();
      void utilsAdmin.providers.user.consultantById.refetch();
    },
    onError: (error) => {
      toast({
        title: t`Error updating consultant`,
        description:
          error instanceof Error ? error.message : t`Something went wrong. Please contact support.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleDisableClick = useCallback(() => {
    if (
      !member.deleted_at &&
      assignmentCounts &&
      (assignmentCounts.enrolmentCount > 0 || assignmentCounts.referralCount > 0)
    ) {
      transferModal.onOpen();
      return;
    }

    deleteConsultant.mutate({
      id: member.id,
      providerId: Number(providerId),
    });
  }, [member.deleted_at, member.id, assignmentCounts, providerId, transferModal, deleteConsultant]);

  return (
    <Box p={4}>
      <Stack borderWidth="1px" borderRadius="md" p={6} flex="1">
        <Flex justify="space-between" align="center">
          <VStack alignItems="start" spacing="0">
            <Text fontWeight="medium" color="fg.emphasized">
              <Trans>Delete this consultant</Trans>
            </Text>
            <Text color="fg.muted" textStyle="sm">
              <Trans>Once you delete a consultant, you can re-enable later</Trans>
            </Text>
          </VStack>
          <Box>
            <Button
              aria-label={!member.deleted_at ? t`Delete consultant` : t`Re-enable consultant`}
              size={'sm'}
              variant="solid"
              borderColor={!member.deleted_at ? 'red.500' : 'purple.500'}
              colorScheme={!member.deleted_at ? 'red' : 'purple'}
              leftIcon={!member.deleted_at ? <UserRoundMinus /> : <UserRoundPlus />}
              isLoading={deleteConsultant.isPending}
              onClick={handleDisableClick}
            >
              {!member.deleted_at ? (
                <Trans>Delete consultant</Trans>
              ) : (
                <Trans>Re-enable consultant</Trans>
              )}
            </Button>
          </Box>
        </Flex>
      </Stack>

      {assignmentCounts ? (
        <BulkTransferConsultantModal
          isOpen={transferModal.isOpen}
          onClose={transferModal.onClose}
          consultantId={member.id}
          providerId={Number(providerId)}
          enrolmentCount={assignmentCounts.enrolmentCount}
          referralCount={assignmentCounts.referralCount}
        />
      ) : null}
    </Box>
  );
}
